import { useEffect, useMemo, useRef, useState } from "react";
import { api, uploadAndProcess } from "../lib/api";
import { Modal, CrudActions } from "./Crud";
import DatePicker from "./DatePicker";
import {
    Brain, Mic, Square, Pause, Play, Upload, FileText, Plus, Sparkles,
    AlertTriangle, Heart, KeyRound, MessageCircle, ListChecks, Clock,
    CheckCircle2, X, Mail, Calendar as CalIcon, Pencil, Send,
} from "lucide-react";

const TIMELINE_TYPES = [
    { k: "meeting", l: "Meeting", colour: "var(--brand-purple)" },
    { k: "note", l: "Note", colour: "var(--brand-magenta)" },
    { k: "decision", l: "Decision", colour: "var(--status-green)" },
    { k: "task", l: "Task", colour: "var(--brand-orange)" },
    { k: "document", l: "Document", colour: "var(--status-amber)" },
    { k: "follow_up", l: "Follow-up", colour: "var(--status-purple)" },
    { k: "update", l: "Update", colour: "var(--brand-purple)" },
];

const typeMeta = (t) => TIMELINE_TYPES.find(x => x.k === t) || { l: t, colour: "var(--brand-purple)" };

export default function ClientMemory({ clientId, clientName }) {
    const [snapshot, setSnapshot] = useState(null);
    const [tab, setTab] = useState("snapshot");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/clients/${clientId}/memory`);
            setSnapshot(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load().catch(() => {}); }, [clientId]);

    if (loading || !snapshot) {
        return <div className="card-warm p-6 text-sm text-[var(--text-ink)]/55">Loading client memory…</div>;
    }

    return (
        <div data-testid="client-memory" className="mb-6">
            <ClientMemorySnapshot snapshot={snapshot} onChange={load} clientId={clientId} clientName={clientName} />
            <div className="flex gap-2 my-4 overflow-x-auto scrollbar-thin">
                {[
                    { k: "snapshot", l: "Snapshot", I: Sparkles },
                    { k: "timeline", l: "Timeline", I: Clock },
                    { k: "meeting", l: "Meeting Hub", I: Mic },
                    { k: "ask", l: "Ask AI", I: MessageCircle },
                ].map(t => (
                    <button key={t.k} onClick={() => setTab(t.k)} data-testid={`cm-tab-${t.k}`}
                            className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap ${tab === t.k ? "brand-gradient text-white" : "btn-ghost"}`}>
                        <t.I size={12} /> {t.l}
                    </button>
                ))}
            </div>
            {tab === "snapshot" && (
                <ManualEntryPanel clientId={clientId} onSaved={load} />
            )}
            {tab === "timeline" && (
                <ClientTimeline clientId={clientId} onChange={load} />
            )}
            {tab === "meeting" && (
                <MeetingHub clientId={clientId} clientName={clientName} onApplied={load} />
            )}
            {tab === "ask" && (
                <ClientAskAi clientId={clientId} clientName={clientName} />
            )}
        </div>
    );
}

function ClientMemorySnapshot({ snapshot, onChange, clientId }) {
    const last = snapshot.last_interaction;
    const tasks = snapshot.outstanding_tasks || [];
    const concerns = snapshot.concerns || [];
    const facts = snapshot.key_facts || [];
    const preferences = snapshot.preferences || [];

    const removeFrom = async (coll, id) => {
        await api.delete(`/${coll}/${id}`);
        onChange?.();
    };

    return (
        <div className="card-warm p-4 sm:p-5 border-l-4 overflow-hidden" style={{ borderLeftColor: "var(--brand-purple)" }}
             data-testid="cm-snapshot">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
                <Brain size={18} className="text-[var(--brand-purple)] shrink-0" />
                <div className="font-serif text-lg sm:text-xl">Client Memory Snapshot</div>
                <span className="text-xs text-[var(--text-ink)]/55 sm:ml-2 basis-full sm:basis-auto">{snapshot.timeline_count} timeline entries</span>
            </div>
            <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
                <Section icon={CalIcon} title="Last meeting / interaction" colour="var(--brand-purple)">
                    {last ? (
                        <div className="text-sm min-w-0">
                            <div className="font-medium break-words">{last.summary}</div>
                            <div className="text-xs text-[var(--text-ink)]/55 mt-0.5 break-words">
                                {(last.date || "").slice(0, 10)} · {typeMeta(last.type).l}
                            </div>
                            {last.details && <div className="text-sm mt-1 text-[var(--text-ink)]/75 line-clamp-3 break-words">{last.details}</div>}
                        </div>
                    ) : <div className="text-sm text-[var(--text-ink)]/55">No interactions logged yet.</div>}
                </Section>

                <Section icon={ListChecks} title="Outstanding tasks" colour="var(--brand-orange)">
                    {tasks.length === 0 ? (
                        <div className="text-sm text-[var(--text-ink)]/55">No outstanding tasks.</div>
                    ) : (
                        <ul className="space-y-1.5">
                            {tasks.slice(0, 6).map(t => (
                                <li key={t.id} className="flex items-start gap-2 text-sm" data-testid={`cm-task-${t.id}`}>
                                    <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0"
                                          style={{ background: t.priority === "high" ? "var(--status-red)" : t.priority === "low" ? "var(--text-ink)" : "var(--brand-orange)" }} />
                                    <div className="flex-1 min-w-0">
                                        <div className="truncate">{t.title}</div>
                                        {t.due_date && <div className="text-[10px] text-[var(--text-ink)]/55">Due {t.due_date}</div>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Section>

                <Section icon={AlertTriangle} title="Client concerns" colour="var(--brand-magenta)">
                    <ChipList items={concerns} onRemove={(c) => removeFrom("client_concerns", c.id)}
                              empty="No concerns logged." testid="cm-concerns" />
                </Section>

                <Section icon={Heart} title="Preferences" colour="var(--status-purple)">
                    <ChipList items={preferences} onRemove={(c) => removeFrom("client_preferences", c.id)}
                              empty="No preferences saved." testid="cm-preferences" />
                </Section>

                <Section icon={KeyRound} title="Key facts & context" colour="var(--brand-purple)"
                         className="lg:col-span-2">
                    <ChipList items={facts} onRemove={(c) => removeFrom("client_facts", c.id)}
                              empty="No key facts saved yet."
                              testid="cm-facts" />
                </Section>
            </div>
            <div className="text-[11px] text-[var(--text-ink)]/55 mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                <Sparkles size={11} className="shrink-0" /> <span className="break-words">Snapshot updates automatically when you log meetings, accept AI extractions, or add notes.</span>
                <span className="basis-full sm:basis-auto sm:ml-1">client_id: <span className="font-mono">{clientId.slice(-6)}</span></span>
            </div>
        </div>
    );
}

function Section({ icon: Icon, title, colour, children, className = "" }) {
    return (
        <div className={`bg-[rgba(26,19,24,0.03)] rounded-xl p-3 sm:p-4 min-w-0 break-words ${className}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color: colour }} className="shrink-0" />
                <div className="font-serif text-base break-words min-w-0">{title}</div>
            </div>
            {children}
        </div>
    );
}

function ChipList({ items, onRemove, empty, testid }) {
    if (!items.length) return <div className="text-sm text-[var(--text-ink)]/55">{empty}</div>;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={testid}>
            {items.map(c => (
                <span key={c.id}
                      className="text-xs pl-3 pr-2 py-1 rounded-full bg-white border border-[rgba(26,19,24,0.12)] inline-flex items-start gap-1.5 max-w-full break-words">
                    <span className="min-w-0 break-words">{c.text}</span>
                    <button onClick={() => onRemove(c)} className="text-[var(--text-ink)]/40 hover:text-[var(--brand-magenta)] shrink-0 mt-0.5"
                            data-testid={`cm-chip-remove-${c.id}`}>
                        <X size={11} />
                    </button>
                </span>
            ))}
        </div>
    );
}

function ManualEntryPanel({ clientId, onSaved }) {
    const [kind, setKind] = useState("note");
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            if (kind === "concern") {
                await api.post("/client_concerns", { client_id: clientId, text });
            } else if (kind === "preference") {
                await api.post("/client_preferences", { client_id: clientId, text });
            } else if (kind === "fact") {
                await api.post("/client_facts", { client_id: clientId, text });
            } else if (kind === "task") {
                await api.post("/tasks", { client_id: clientId, title: text, status: "incomplete", priority: "normal" });
            } else {
                await api.post(`/clients/${clientId}/timeline`, { type: kind, summary: text });
            }
            setText("");
            onSaved?.();
        } finally { setBusy(false); }
    };

    const opts = [
        { k: "note", l: "Note" },
        { k: "meeting", l: "Meeting" },
        { k: "decision", l: "Decision" },
        { k: "update", l: "Update" },
        { k: "follow_up", l: "Follow-up" },
        { k: "task", l: "Task" },
        { k: "concern", l: "Concern" },
        { k: "preference", l: "Preference" },
        { k: "fact", l: "Key fact" },
    ];

    return (
        <div className="card-warm p-4" data-testid="cm-manual">
            <div className="flex items-center gap-2 mb-3">
                <Pencil size={14} className="text-[var(--brand-magenta)]" />
                <div className="font-serif text-base">Add to memory</div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
                {opts.map(o => (
                    <button key={o.k} onClick={() => setKind(o.k)} data-testid={`cm-kind-${o.k}`}
                            className={`text-xs px-3 py-1.5 rounded-full ${kind === o.k ? "brand-gradient text-white" : "btn-ghost"}`}>
                        {o.l}
                    </button>
                ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)}
                          placeholder={`Add a ${kind.replace("_", " ")}…`}
                          data-testid="cm-manual-text"
                          rows={2}
                          className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                <button onClick={save} disabled={busy} data-testid="cm-manual-save" className="btn-primary text-sm shrink-0 sm:self-stretch">
                    {busy ? "…" : "Save"}
                </button>
            </div>
        </div>
    );
}

function ClientTimeline({ clientId, onChange }) {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ type: "note", summary: "", details: "", date: "" });

    const load = async () => {
        const { data } = await api.get(`/clients/${clientId}/timeline`);
        setItems(data);
    };
    useEffect(() => { load().catch(() => {}); }, [clientId]);

    const save = async () => {
        if (!form.summary.trim()) return;
        setBusy(true);
        try {
            await api.post(`/clients/${clientId}/timeline`, form);
            setForm({ type: "note", summary: "", details: "", date: "" });
            await load(); onChange?.();
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this entry?")) return;
        await api.delete(`/client_timeline/${id}`);
        await load(); onChange?.();
    };

    return (
        <div data-testid="cm-timeline">
            <div className="card-warm p-4 grid sm:grid-cols-[140px_1fr_140px_auto] gap-2 mb-4">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                        data-testid="cm-tl-type"
                        className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    {TIMELINE_TYPES.map(t => <option key={t.k} value={t.k}>{t.l}</option>)}
                </select>
                <input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
                       placeholder="Short summary (e.g. Discussed tourist licence transfer)"
                       data-testid="cm-tl-summary"
                       className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })}
                            testid="cm-tl-date" placeholder="Date" />
                <button onClick={save} disabled={busy} data-testid="cm-tl-save" className="btn-primary text-sm">
                    <Plus size={14} className="inline" /> Add
                </button>
                <textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
                          placeholder="Optional details / notes"
                          rows={2}
                          data-testid="cm-tl-details"
                          className="sm:col-span-4 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
            </div>
            {items.length === 0 ? (
                <div className="card-warm p-6 text-sm text-[var(--text-ink)]/55 text-center">No timeline entries yet.</div>
            ) : (
                <ol className="relative pl-6" data-testid="cm-tl-list">
                    <div className="absolute left-2 top-2 bottom-2 w-px bg-[rgba(26,19,24,0.1)]" />
                    {items.map(e => {
                        const meta = typeMeta(e.type);
                        return (
                            <li key={e.id} className="relative mb-4">
                                <span className="absolute -left-[1.05rem] top-1 w-3 h-3 rounded-full ring-2 ring-[var(--bg-cream)]"
                                      style={{ background: meta.colour }} />
                                <div className="card-warm p-3" data-testid={`cm-tl-item-${e.id}`}>
                                    <div className="flex items-start gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] uppercase tracking-wide" style={{ color: meta.colour }}>
                                                {meta.l} · {(e.date || "").slice(0, 10)}
                                            </div>
                                            <div className="text-sm font-medium mt-0.5 break-words">{e.summary}</div>
                                            {e.details && <div className="text-sm text-[var(--text-ink)]/75 mt-1 whitespace-pre-wrap break-words">{e.details}</div>}
                                            {(e.related_task_ids || []).length > 0 && (
                                                <div className="text-[10px] text-[var(--text-ink)]/55 mt-1">
                                                    Linked tasks: {e.related_task_ids.length}
                                                </div>
                                            )}
                                        </div>
                                        <CrudActions idBase={`tl-${e.id}`} onDelete={() => remove(e.id)} />
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}

function MeetingHub({ clientId, clientName, onApplied }) {
    const [mode, setMode] = useState("record"); // record | upload | text
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [transcript, setTranscript] = useState("");
    const [pastedText, setPastedText] = useState("");
    const [meetingTitle, setMeetingTitle] = useState("");
    const [notes, setNotes] = useState(null);
    const [extraction, setExtraction] = useState(null);
    const [followUp, setFollowUp] = useState(null);
    const [busy, setBusy] = useState("");
    const [applied, setApplied] = useState(false);
    const [applyBusy, setApplyBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const [paused, setPaused] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const intervalRef = useRef(null);

    const startRec = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            rec.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                setAudioBlob(blob);
                stream.getTracks().forEach(t => t.stop());
            };
            rec.start(1000);
            recRef.current = rec;
            setRecording(true); setPaused(false); setSeconds(0);
            intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
        } catch (e) {
            alert("Microphone permission denied: " + e.message);
        }
    };
    const pauseRec = () => { recRef.current?.pause(); setPaused(true); clearInterval(intervalRef.current); };
    const resumeRec = () => { recRef.current?.resume(); setPaused(false); intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000); };
    const stopRec = () => { recRef.current?.stop(); setRecording(false); setPaused(false); clearInterval(intervalRef.current); };

    const transcribeAndAnalyse = async () => {
        let src = mode === "record" ? audioBlob : audioFile;
        let tr = "";
        if (mode === "text") {
            tr = pastedText.trim();
            if (!tr) { alert("Paste meeting notes first."); return; }
            setTranscript(tr);
        } else {
            if (!src) { alert("Add audio first."); return; }
            // File-like from a recorded Blob (webm) or an uploaded audio.
            const audio = mode === "record"
                ? new File([src], "meeting.webm", { type: src.type || "audio/webm" })
                : src;
            try {
                // Async upload + background Whisper — no 30 s timeouts.
                const data = await uploadAndProcess({
                    files: audio,
                    kind: "audio_transcribe",
                    onStatus: (s, info) => {
                        if (s === "uploading") {
                            const pct = typeof info?.percent === "number" ? info.percent : 0;
                            setBusy(`Uploading… ${pct}%`);
                        } else if (s === "processing") {
                            setBusy("Processing document…");
                        } else if (s === "complete") {
                            setBusy("Transcription complete");
                        }
                    },
                });
                tr = data.text || "";
                setTranscript(tr);
            } catch (e) {
                setBusy("");
                alert("Transcription failed: " + (e.friendly || e.response?.data?.detail || e.message));
                return;
            }
        }
        try {
            setBusy("Generating smart meeting notes…");
            const { data: n } = await api.post("/ai/meeting-notes", {
                transcript: tr, title: meetingTitle, client_name: clientName,
            });
            setNotes(n);
            setBusy("Extracting action items…");
            const { data: ex } = await api.post("/ai/meeting-extract", {
                text: tr, client_name: clientName,
            });
            setExtraction(ex);
        } catch (e) {
            alert("AI analysis failed: " + (e.response?.data?.detail || e.message));
        } finally { setBusy(""); }
    };

    const generateFollowUp = async () => {
        setBusy("Drafting follow-up email…");
        try {
            const { data } = await api.post("/ai/follow-up-email", {
                transcript, notes: JSON.stringify(notes || {}),
                client_name: clientName, tone: "professional",
            });
            setFollowUp(data);
        } finally { setBusy(""); }
    };

    const applyExtraction = async (sel) => {
        setApplyBusy(true);
        try {
            const payload = {
                source: "meeting",
                source_summary: meetingTitle || "Extracted from meeting",
                details: (transcript || pastedText || "").slice(0, 4000),
                tasks: sel.tasks.filter(t => t.accept && t.title),
                concerns: sel.concerns.filter(c => c.accept).map(c => c.text),
                preferences: sel.preferences.filter(c => c.accept).map(c => c.text),
                key_facts: [
                    ...sel.key_facts.filter(c => c.accept).map(c => c.text),
                    ...sel.decisions.filter(c => c.accept).map(c => "Decision: " + c.text),
                ],
                follow_ups: sel.follow_ups.filter(f => f.accept),
            };
            await api.post(`/clients/${clientId}/apply-suggestions`, payload);
            setApplied(true);
            onApplied?.();
        } finally { setApplyBusy(false); }
    };

    const resetMeeting = () => {
        setApplied(false);
        setExtraction(null);
        setNotes(null);
        setFollowUp(null);
        setTranscript("");
        setPastedText("");
        setAudioBlob(null);
        setAudioFile(null);
        setMeetingTitle("");
    };

    const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
    const ready = !!(transcript || pastedText);

    return (
        <div className="space-y-4" data-testid="cm-meeting-hub">
            <div className="card-warm p-4">
                <div className="font-serif text-base mb-2">1 · Capture the meeting</div>
                <input value={meetingTitle} onChange={(e) => setMeetingTitle(e.target.value)}
                       placeholder="Meeting title (optional)"
                       data-testid="cm-meeting-title"
                       className="w-full mb-3 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                <div className="flex flex-wrap gap-1.5 mb-3">
                    {[{ k: "record", l: "Record live" }, { k: "upload", l: "Upload audio" }, { k: "text", l: "Paste notes" }].map(o => (
                        <button key={o.k} onClick={() => setMode(o.k)} data-testid={`cm-meeting-mode-${o.k}`}
                                className={`text-xs px-3 py-1.5 rounded-full ${mode === o.k ? "brand-gradient text-white" : "btn-ghost"}`}>
                            {o.l}
                        </button>
                    ))}
                </div>

                {mode === "record" && (
                    <div className="bg-[rgba(26,19,24,0.03)] rounded-xl p-3 flex flex-wrap items-center gap-3">
                        <div className={`w-3 h-3 rounded-full shrink-0 ${recording && !paused ? "bg-[var(--status-red)] animate-pulse" : "bg-[var(--text-ink)]/20"}`} />
                        <div className="font-mono text-xl">{mmss}</div>
                        <div className="flex-1 min-w-0" />
                        {!recording && !audioBlob && (
                            <button onClick={startRec} className="btn-primary text-sm inline-flex items-center gap-2"
                                    data-testid="cm-rec-start"><Mic size={14} /> Start</button>
                        )}
                        {recording && !paused && (
                            <>
                                <button onClick={pauseRec} className="btn-ghost text-sm"><Pause size={14} /> Pause</button>
                                <button onClick={stopRec} className="btn-primary text-sm"
                                        data-testid="cm-rec-stop"><Square size={14} /> Stop</button>
                            </>
                        )}
                        {recording && paused && (
                            <>
                                <button onClick={resumeRec} className="btn-ghost text-sm"><Play size={14} /> Resume</button>
                                <button onClick={stopRec} className="btn-primary text-sm">Stop</button>
                            </>
                        )}
                        {!recording && audioBlob && (
                            <span className="text-xs text-[var(--text-ink)]/65">Captured · {(audioBlob.size / 1024).toFixed(0)} KB</span>
                        )}
                    </div>
                )}

                {mode === "upload" && (
                    <label className="bg-[rgba(26,19,24,0.03)] rounded-xl p-3 text-sm cursor-pointer hover:bg-[rgba(255,107,53,0.06)] transition flex items-center gap-2">
                        <Upload size={16} className="shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{audioFile ? audioFile.name : "Choose meeting audio (.mp3, .m4a, .wav, .webm — up to 25 MB)"}</span>
                        <input type="file" accept="audio/*" onChange={(e) => setAudioFile(e.target.files[0])}
                               data-testid="cm-meeting-audio-file" className="hidden" />
                    </label>
                )}

                {mode === "text" && (
                    <textarea value={pastedText} onChange={(e) => setPastedText(e.target.value)}
                              placeholder="Paste meeting notes, transcript, or a manual update…"
                              rows={4}
                              data-testid="cm-meeting-text"
                              className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                )}

                <div className="mt-3 flex justify-end">
                    <button onClick={transcribeAndAnalyse} disabled={!!busy} data-testid="cm-meeting-analyse"
                            className="btn-primary text-sm">
                        {busy || "Transcribe & analyse"}
                    </button>
                </div>
            </div>

            {transcript && (
                <details className="card-warm p-4" data-testid="cm-transcript">
                    <summary className="font-serif text-base cursor-pointer">2 · Transcript</summary>
                    <div className="mt-2 text-sm whitespace-pre-wrap max-h-64 overflow-auto scrollbar-thin">
                        {transcript}
                    </div>
                </details>
            )}

            {notes && (
                <div className="card-warm p-4" data-testid="cm-meeting-notes">
                    <div className="font-serif text-base mb-2">3 · Smart meeting notes</div>
                    <div className="text-sm text-[var(--text-ink)]/80 mb-3">{notes.summary}</div>
                    {(notes.attendees || []).length > 0 && (
                        <div className="text-xs text-[var(--text-ink)]/65 mb-2">
                            <span className="uppercase tracking-wide">Attendees</span>: {notes.attendees.join(", ")}
                        </div>
                    )}
                    {(notes.topics_discussed || []).map((t, i) => (
                        <div key={i} className="mb-2">
                            <div className="font-medium text-sm">{t.heading}</div>
                            <ul className="text-sm list-disc pl-5">{(t.points || []).map((p, j) => <li key={j}>{p}</li>)}</ul>
                        </div>
                    ))}
                    {(notes.decisions || []).length > 0 && (
                        <div className="text-sm mt-2">
                            <div className="font-medium">Decisions</div>
                            <ul className="list-disc pl-5">{notes.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul>
                        </div>
                    )}
                    {(notes.questions_raised || []).length > 0 && (
                        <div className="text-sm mt-2">
                            <div className="font-medium">Questions raised</div>
                            <ul className="list-disc pl-5">{notes.questions_raised.map((d, i) => <li key={i}>{d}</li>)}</ul>
                        </div>
                    )}
                    {notes.next_meeting && (
                        <div className="text-xs text-[var(--text-ink)]/70 mt-2 italic">Next: {notes.next_meeting}</div>
                    )}
                </div>
            )}

            {extraction && (
                <ExtractionApprovals extraction={extraction}
                                     applied={applied}
                                     busy={applyBusy}
                                     onApply={applyExtraction}
                                     onReset={resetMeeting} />
            )}

            {ready && notes && (
                <div className="card-warm p-4" data-testid="cm-followup">
                    <div className="font-serif text-base mb-2 inline-flex items-center gap-2">
                        <Mail size={14} className="text-[var(--brand-orange)]" /> 5 · Follow-up email
                    </div>
                    {!followUp ? (
                        <button onClick={generateFollowUp} disabled={!!busy} data-testid="cm-followup-gen"
                                className="btn-primary text-sm inline-flex items-center gap-2">
                            <Send size={14} /> Draft follow-up email
                        </button>
                    ) : (
                        <FollowUpDraft draft={followUp} setDraft={setFollowUp} />
                    )}
                </div>
            )}
        </div>
    );
}

function ExtractionApprovals({ extraction, applied, busy, onApply, onReset }) {
    const initial = useMemo(() => ({
        tasks: (extraction.tasks || []).map(t => ({ ...t, accept: true })),
        concerns: (extraction.concerns || []).map(t => ({ text: t, accept: true })),
        preferences: (extraction.preferences || []).map(t => ({ text: t, accept: true })),
        key_facts: (extraction.key_facts || []).map(t => ({ text: t, accept: true })),
        decisions: (extraction.decisions || []).map(t => ({ text: t, accept: true })),
        follow_ups: (extraction.follow_ups || []).map(t => ({ ...t, accept: true })),
    }), [extraction]);
    const [sel, setSel] = useState(initial);

    const toggle = (group, i) => setSel(s => {
        const next = { ...s, [group]: [...s[group]] };
        next[group][i] = { ...next[group][i], accept: !next[group][i].accept };
        return next;
    });
    const editText = (group, i, v) => setSel(s => {
        const next = { ...s, [group]: [...s[group]] };
        next[group][i] = { ...next[group][i], text: v };
        return next;
    });
    const editTaskField = (i, field, v) => setSel(s => {
        const next = { ...s, tasks: [...s.tasks] };
        next.tasks[i] = { ...next.tasks[i], [field]: v };
        return next;
    });

    if (applied) {
        const accepted = {
            tasks: sel.tasks.filter(t => t.accept).length,
            concerns: sel.concerns.filter(t => t.accept).length,
            preferences: sel.preferences.filter(t => t.accept).length,
            key_facts: sel.key_facts.filter(t => t.accept).length,
            decisions: sel.decisions.filter(t => t.accept).length,
        };
        const total = Object.values(accepted).reduce((a, b) => a + b, 0);
        return (
            <div className="card-warm p-4 border-l-4"
                 style={{ borderLeftColor: "var(--status-green)" }} data-testid="cm-extraction-applied">
                <div className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[var(--status-green)] mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="font-serif text-base">Saved to Client Memory</div>
                        <div className="text-xs text-[var(--text-ink)]/65 mt-0.5">
                            {total} item{total === 1 ? "" : "s"} added · {accepted.tasks} task{accepted.tasks === 1 ? "" : "s"},
                            {" "}{accepted.concerns} concern{accepted.concerns === 1 ? "" : "s"},
                            {" "}{accepted.preferences} preference{accepted.preferences === 1 ? "" : "s"},
                            {" "}{accepted.key_facts + accepted.decisions} key fact{(accepted.key_facts + accepted.decisions) === 1 ? "" : "s"}.
                        </div>
                        <button onClick={onReset} data-testid="cm-meeting-reset"
                                className="btn-ghost text-xs mt-3 inline-flex items-center gap-1">
                            <Plus size={12} /> Capture another meeting
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card-warm p-4" data-testid="cm-extraction">
            <div className="font-serif text-base mb-1">4 · Suggested for Client Memory</div>
            <div className="text-xs text-[var(--text-ink)]/55 mb-3">Untick anything you don&apos;t want saved. Edit text inline before saving.</div>

            {sel.tasks.length > 0 && (
                <Group title="Tasks">
                    {sel.tasks.map((t, i) => (
                        <div key={i} className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_120px_100px] gap-2 items-center text-sm py-1">
                            <input type="checkbox" checked={t.accept} onChange={() => toggle("tasks", i)}
                                   data-testid={`cm-ext-task-${i}`} />
                            <input value={t.title} onChange={(e) => editTaskField(i, "title", e.target.value)}
                                   className="min-w-0 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.12)] text-sm" />
                            <div className="col-start-2 sm:col-auto">
                                <DatePicker value={t.due_date || ""} onChange={(v) => editTaskField(i, "due_date", v)}
                                            testid={`cm-task-due-${i}`} placeholder="Due" clearable />
                            </div>
                            <select value={t.priority || "normal"} onChange={(e) => editTaskField(i, "priority", e.target.value)}
                                    className="col-start-2 sm:col-auto min-w-0 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.12)] text-xs">
                                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
                            </select>
                        </div>
                    ))}
                </Group>
            )}
            <TextGroup title="Concerns" items={sel.concerns} group="concerns" toggle={toggle} edit={editText} testid="cm-ext-concern" />
            <TextGroup title="Preferences" items={sel.preferences} group="preferences" toggle={toggle} edit={editText} testid="cm-ext-pref" />
            <TextGroup title="Key facts" items={sel.key_facts} group="key_facts" toggle={toggle} edit={editText} testid="cm-ext-fact" />
            <TextGroup title="Decisions" items={sel.decisions} group="decisions" toggle={toggle} edit={editText} testid="cm-ext-dec" />

            <div className="mt-3 flex justify-end">
                <button onClick={() => onApply(sel)} disabled={busy} data-testid="cm-ext-apply" className="btn-primary text-sm">
                    {busy ? "Saving…" : "Save to Client Memory"}
                </button>
            </div>
        </div>
    );
}

function Group({ title, children }) {
    return (
        <div className="mb-3">
            <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55 mb-1">{title}</div>
            <div>{children}</div>
        </div>
    );
}

function TextGroup({ title, items, group, toggle, edit, testid }) {
    if (!items || items.length === 0) return null;
    return (
        <Group title={title}>
            {items.map((c, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr] gap-2 items-center text-sm py-1">
                    <input type="checkbox" checked={c.accept} onChange={() => toggle(group, i)}
                           data-testid={`${testid}-${i}`} />
                    <input value={c.text} onChange={(e) => edit(group, i, e.target.value)}
                           className="min-w-0 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.12)] text-sm" />
                </div>
            ))}
        </Group>
    );
}

function FollowUpDraft({ draft, setDraft }) {
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
            alert("Copied to clipboard");
        } catch { alert("Copy failed"); }
    };
    const mailto = `mailto:?subject=${encodeURIComponent(draft.subject || "")}&body=${encodeURIComponent(draft.body || "")}`;
    return (
        <div className="space-y-2">
            <input value={draft.subject || ""} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                   data-testid="cm-followup-subject"
                   className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm font-medium" />
            <textarea value={draft.body || ""} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                      rows={10}
                      data-testid="cm-followup-body"
                      className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm font-mono" />
            <div className="flex gap-2 justify-end">
                <button onClick={copy} className="btn-ghost text-sm">Copy</button>
                <a href={mailto} className="btn-primary text-sm inline-flex items-center gap-2"
                   data-testid="cm-followup-mailto"><Send size={12} /> Open in email client</a>
            </div>
        </div>
    );
}

function ClientAskAi({ clientId, clientName }) {
    const [q, setQ] = useState("");
    const [log, setLog] = useState([]);
    const [busy, setBusy] = useState(false);

    const QUICK = [
        "What should I know before speaking to this client?",
        "What happened in the last meeting?",
        "What actions are still outstanding?",
        "What deadlines are coming up?",
        "What concerns has this client raised?",
        "Summarise this client's project history.",
        "What do I need to do before the next meeting?",
    ];

    const ask = async (preset) => {
        const question = (preset ?? q).trim();
        if (!question) return;
        setQ(""); setBusy(true);
        const next = [...log, { role: "user", text: question }];
        setLog(next);
        try {
            const { data } = await api.post("/ai/client-ask", {
                client_id: clientId, question, history: next,
            });
            setLog(l => [...l, { role: "ai", text: data.answer }]);
        } catch (e) {
            setLog(l => [...l, { role: "ai", text: "Sorry — " + (e.response?.data?.detail || e.message) }]);
        } finally { setBusy(false); }
    };

    return (
        <div className="card-warm p-4" data-testid="cm-ask">
            <div className="flex items-center gap-2 mb-2">
                <MessageCircle size={14} className="text-[var(--brand-purple)]" />
                <div className="font-serif text-base">Ask AI about {clientName}</div>
            </div>
            <div className="text-xs text-[var(--text-ink)]/55 mb-2">
                Answers come strictly from this client&apos;s saved memory, tasks, timeline, projects and documents.
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
                {QUICK.map((qq, i) => (
                    <button key={i} onClick={() => ask(qq)} disabled={busy} data-testid={`cm-ask-quick-${i}`}
                            className="text-xs px-3 py-1.5 rounded-full btn-ghost">{qq}</button>
                ))}
            </div>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto scrollbar-thin mb-3">
                {log.map((m, i) => (
                    <div key={i} className={`rounded-xl p-3 text-sm min-w-0 ${m.role === "user" ? "bg-[rgba(123,45,142,0.06)]" : "bg-[rgba(26,19,24,0.03)]"}`}>
                        <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55 mb-1">
                            {m.role === "user" ? "You" : "Client Memory"}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    </div>
                ))}
                {busy && <div className="text-xs text-[var(--text-ink)]/55">Thinking…</div>}
            </div>
            <div className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && ask()}
                       placeholder="Ask anything about this client…"
                       data-testid="cm-ask-input"
                       className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                <button onClick={() => ask()} disabled={busy} data-testid="cm-ask-send" className="btn-primary text-sm shrink-0">Ask</button>
            </div>
        </div>
    );
}
